import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Star, Quote } from 'lucide-react';

const testimonials = [
  {
    name: "Marie L.",
    location: "Lausanne",
    stars: 5,
    text: "Déménagement impeccable, équipe ponctuelle et soigneuse. Je recommande vivement Janyel's pour tout déménagement en Suisse romande.",
    initials: "ML",
  },
  {
    name: "Pierre D.",
    location: "Genève",
    stars: 5,
    text: "Transport de marchandises rapide et professionnel. Communication excellente du début à la fin. Prix très compétitif.",
    initials: "PD",
  },
  {
    name: "Sophie M.",
    location: "Berne",
    stars: 4,
    halfStar: true,
    text: "Équipe très professionnelle et attentive. Notre déménagement s'est déroulé sans le moindre souci, tout était bien organisé et sécurisé.",
    initials: "SM",
  },
];

function StarRating({ stars, halfStar }) {
  return (
    <div className="flex gap-1 mb-5">
      {[...Array(stars)].map((_, i) => (
        <Star key={i} className="w-4 h-4 fill-[#FFD700] text-[#FFD700]" />
      ))}
      {halfStar && (
        <div className="relative w-4 h-4">
          <Star className="w-4 h-4 text-[#FFD700] absolute" />
          <div className="overflow-hidden w-[50%] absolute">
            <Star className="w-4 h-4 fill-[#FFD700] text-[#FFD700]" />
          </div>
        </div>
      )}
    </div>
  );
}

export default function Testimonials() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="temoignages" ref={ref} className="py-24 md:py-32 bg-[#0A0A0A]" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <div className="w-16 h-1 bg-[#FF0000] mx-auto mb-6" />
          <h2 className="font-['Oswald'] text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-tight text-white" data-testid="testimonials-title">
            Ce que disent nos clients
          </h2>
          <p className="text-white/50 mt-4 text-base md:text-lg">
            La satisfaction de nos clients est notre meilleure publicité.
          </p>
        </motion.div>

        {/* Testimonial cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {testimonials.map((t, index) => (
            <motion.div
              key={t.name}
              initial={{ opacity: 0, y: 40 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.15 }}
              className="testimonial-card bg-[#121212] border border-[#2A2A2A] p-8 relative"
              data-testid={`testimonial-card-${index}`}
            >
              {/* Quote icon */}
              <Quote className="w-10 h-10 text-[#FF0000]/20 absolute top-6 right-6" />

              {/* Stars */}
              <StarRating stars={t.stars} halfStar={t.halfStar} />

              {/* Text */}
              <p className="text-white/70 leading-relaxed mb-6 text-sm">
                "{t.text}"
              </p>

              {/* Author */}
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 bg-[#FF0000]/10 border border-[#FF0000]/30 flex items-center justify-center text-[#FF0000] font-bold text-sm">
                  {t.initials}
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">{t.name}</p>
                  <p className="text-white/40 text-xs">{t.location}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
